/** Scrapers — top-level page with two tabs: the Job Console (create/manage the
 * crawlers, Phase 21) and Health (read-only monitoring, Phase 20). v1 kept these
 * on one screen; v2 splits them into tabs so the dense builder and the health
 * feed each get room. */
import { useRef, useState } from "react";
import { ScraperJobsConsole } from "./ScraperJobsConsole";
import { ScraperHealthPage } from "./ScraperHealthPage";
import { WholesalerReviewTab } from "./WholesalerReviewTab";
import { ZillowFilterPurge } from "./ZillowFilterPurge";
import { MarkSheetSync } from "./MarkSheetSync";
import { PortalHealthCard } from "./PortalHealthCard";
import { RealtorSession } from "./RealtorSession";
import { ProxyStatusBanner } from "./ProxyStatusBanner";
import type { ScraperIncident, FetchStats, ScraperSource, ScraperJob } from "./api";
import type { QueueItem } from "./wholesalerQueue";

export function ScrapersPage(props: {
  loadHealth: () => Promise<ScraperIncident[]>;
  loadStats: () => Promise<FetchStats>;
  loadJobs: () => Promise<Record<ScraperSource, ScraperJob[]>>;
  onSave: (job: { id?: string; source: ScraperSource; name: string; config: Record<string, unknown>; enabled: boolean }) => Promise<string>;
  onToggle: (id: string, enabled: boolean) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onPauseAll: (enabled: boolean) => Promise<number>;
  canEdit: boolean;
  queue: QueueItem[];
  onApproveQueue: (item: QueueItem) => void;
  onRejectQueue: (item: QueueItem) => void;
  token: string | null;
  isAdmin: boolean;
}) {
  // The sub-tab survives a page remount (Tom 2026-08-15: "loads then gone
  // after 2 seconds" — a parent re-render that remounts this page was
  // resetting tab back to "console", so Health vanished under him). Same
  // localStorage pattern as crm2_view.
  type Tab = "console" | "review" | "health";
  const [tab, setTabState] = useState<Tab>(() => {
    try {
      const t = localStorage.getItem("crm2_scrapers_tab");
      return t === "review" || t === "health" ? t : "console";
    } catch { return "console"; }
  });
  const setTab = (t: Tab) => {
    setTabState(t);
    try { localStorage.setItem("crm2_scrapers_tab", t); } catch { /* private mode */ }
  };
  // isAdmin is sticky for the session: a transient profile refetch (undefined
  // for a beat) must not blank the admin-only Health cards mid-look. UI-only —
  // the server enforces the real permissions.
  const wasAdmin = useRef(false);
  if (props.isAdmin) wasAdmin.current = true;
  const isAdmin = props.isAdmin || wasAdmin.current;
  return (
    <div className="scrapers-wrap">
      <ProxyStatusBanner loadHealth={props.loadHealth} />
      <div className="sctabs top">
        <button className={"sctab" + (tab === "console" ? " active" : "")} onClick={() => setTab("console")}>Job Console</button>
        <button className={"sctab" + (tab === "review" ? " active" : "")} onClick={() => setTab("review")}>Wholesaler Review{props.queue.length ? ` (${props.queue.length})` : ""}</button>
        <button className={"sctab" + (tab === "health" ? " active" : "")} onClick={() => setTab("health")}>Health</button>
      </div>
      {tab === "console" ? (
        <>
          <MarkSheetSync token={props.token} isAdmin={isAdmin} />
          <ZillowFilterPurge token={props.token} isAdmin={isAdmin} />
          <ScraperJobsConsole
            loadJobs={props.loadJobs}
            onSave={props.onSave}
            onToggle={props.onToggle}
            onDelete={props.onDelete}
            onPauseAll={props.onPauseAll}
            canEdit={props.canEdit}
          />
        </>
      ) : tab === "review" ? (
        <WholesalerReviewTab queue={props.queue} canEdit={props.canEdit} onApprove={props.onApproveQueue} onReject={props.onRejectQueue} />
      ) : (
        <>
          <PortalHealthCard token={props.token} isAdmin={isAdmin} />
          <RealtorSession token={props.token} isAdmin={isAdmin} />
          <ScraperHealthPage loadHealth={props.loadHealth} loadStats={props.loadStats} token={props.token} />
        </>
      )}
    </div>
  );
}
